import React, {useEffect, useMemo, useState} from "react";
import {Button, Drawer, message, Table, Checkbox, Tag} from "antd";
import type {ColumnsType} from "antd/es/table";
import {useMutation, useQuery, useLazyQuery} from "@apollo/client";
import {
    DspDeliveryTaskReviewListResponse,
    GET_DSP_DELIVERY_TASK_REVIEW_LIST
} from "../graphql/queries/getDspDeliveryTaskReviewList";
import {
    DeliveryReviewDetailResponse,
    GET_DELIVERY_TASK_REVIEW_DETAIL
} from "../graphql/queries/getDeliveryTaskReviewDetail";
import {ReviewParcelType} from "../graphql/types/ReviewParcelType";
import {useTranslation} from 'react-i18next';
import {BATCH_MARK_SUSPECTED_MISSING} from "../../../../graphql/mutations/batchMarkSuspectedMissing";
import {useWarehouseContext} from '../../../../contexts/WarehouseContext';
import {SiteType} from "graphql/types/SiteType";
import CentralParcelDetailDrawer from "../../../central/parcels/components/CentralParcelDetailDrawer";
import {getUserInfo} from "../../../../utils/auth";

interface DepartureDspReviewDrawerProps {
    visible: boolean;
    title: string;
    record?: ReviewParcelType;
    onClose: () => void;
    onDataRefresh: () => void;
}

interface DspReviewDetailType {
    id: string;
    area: string;
    unScanCount: number;
    driverName: string;
    orderIdList: number[];
    driverPhone: string;
    driverAccountId: number;
    routeName: string;
}

interface ExpandedRowData {
    [key: string]: {
        loading: boolean;
        data: Array<{
            orderId: string;
            trackingNumber: string;
            routeName: string;
            isTransferIn: boolean;
        }>;
    };
}

const DepartureDspReviewDrawer: React.FC<DepartureDspReviewDrawerProps> = ({
    visible,
    title,
    record,
    onClose,
    onDataRefresh
}) => {
    const { t } = useTranslation();
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedParcels, setSelectedParcels] = useState<DspReviewDetailType[]>([]);
    const [parcels, setParcels] = useState<DspReviewDetailType[]>();
    const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
    const [expandedRowData, setExpandedRowData] = useState<ExpandedRowData>({});
    // 管理展开数据的选中状态：{ [rowKey]: orderId[] }
    const [expandedSelectedOrderIds, setExpandedSelectedOrderIds] = useState<{ [key: string]: string[] }>({});
    const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
    const [parcelDrawerVisible, setParcelDrawerVisible] = useState(false);
    const { currentWarehouse } = useWarehouseContext();
    const siteType = currentWarehouse?.siteType;

    const [getDeliveryTaskReviewDetail, { loading: detailLoading }] = useLazyQuery<DeliveryReviewDetailResponse>(
        GET_DELIVERY_TASK_REVIEW_DETAIL,
        {
            onCompleted: (data) => {
                if (data?.getDeliveryTaskReviewDetail) {
                    // 数据已经在 onCompleted 中处理
                }
            },
            onError: (error) => {
                console.error('Failed to fetch delivery task review detail:', error);
                message.error(t('wms.review.messages.failedToLoadDetail'));
            }
        }
    );

    const {data, loading} = useQuery<DspDeliveryTaskReviewListResponse>(
        GET_DSP_DELIVERY_TASK_REVIEW_LIST,
        {
            variables: {
                dspId: record?.dspId ? parseInt(record.dspId) : 0,
                warehouseId: currentWarehouse!!.id
            },
            skip: !record?.dspId || !visible || !currentWarehouse?.id
        }
    );

    useEffect(() => {
        if (data?.getDspDeliveryTaskReviewList) {
            const detailParcels = data.getDspDeliveryTaskReviewList.map((detail, index) => ({
                id: `dsp-${index}`,
                area: detail.routeName,
                unScanCount: detail.unScanCount,
                driverName: detail.driverName,
                orderIdList: detail.orderIdList,
                driverPhone: detail.driverPhone,
                driverAccountId: detail.driverAccountId,
                routeName: detail.routeName
            }));
            setParcels(detailParcels);
        }
    }, [data]);

    // 每次打开抽屉时，重置展开状态
    useEffect(() => {
        if (visible) {
            setExpandedRowKeys([]);
            setExpandedRowData({});
            setSelectedRowKeys([]);
            setSelectedParcels([]);
            setExpandedSelectedOrderIds({});
        }
    }, [visible]);

    // Batch Mark as Exception Mutation
    const [batchMarkSuspectedMissingMutation, {loading: batchMarkAsExceptionLoading}] = useMutation(
        BATCH_MARK_SUSPECTED_MISSING,
        {
            onCompleted: (data) => {
                if (data.batchMarkSuspectedMissing) {
                    onDataRefresh();
                } else {
                    message.error('Failed to mark as suspected missing');
                }
            },
            onError: (error) => {
                message.error(`${t('wms.review.messages.failedToMarkAsSuspectedMissing')}: ${error.message}`);
            }
        }
    );

    // 计算路线的复选框状态
    const getRowCheckboxState = (record: DspReviewDetailType) => {
        const expandedData = expandedRowData[record.id];
        if (!expandedData || expandedData.data.length === 0) {
            // 如果没有展开数据，根据主表格行的选中状态判断
            const isSelected = selectedRowKeys.includes(record.id);
            return { checked: isSelected, indeterminate: false };
        }
        
        const expandedSelected = expandedSelectedOrderIds[record.id] || [];
        const totalCount = expandedData.data.length;
        const selectedCount = expandedSelected.length;
        
        if (selectedCount === 0) {
            return { checked: false, indeterminate: false };
        } else if (selectedCount === totalCount) {
            return { checked: true, indeterminate: false };
        } else {
            return { checked: false, indeterminate: true };
        }
    };

    // 计算全选框的状态
    const getAllCheckboxState = () => {
        if (!filteredParcels || filteredParcels.length === 0) {
            return { checked: false, indeterminate: false };
        }
        
        let checkedCount = 0;
        let indeterminateCount = 0;
        
        filteredParcels.forEach(record => {
            const checkboxState = getRowCheckboxState(record);
            if (checkboxState.checked) {
                checkedCount++;
            } else if (checkboxState.indeterminate) {
                indeterminateCount++;
            }
        });
        
        const totalCount = filteredParcels.length;
        if (checkedCount === totalCount && indeterminateCount === 0) {
            return { checked: true, indeterminate: false };
        } else if (checkedCount > 0 || indeterminateCount > 0) {
            return { checked: false, indeterminate: true };
        } else {
            return { checked: false, indeterminate: false };
        }
    };

    // 处理行选择
    const handleRowSelectionChange = (selectedKeys: React.Key[], selectedRows: DspReviewDetailType[]) => {
        setSelectedRowKeys(selectedKeys);
        setSelectedParcels(selectedRows);
        
        // 当选中路线时，自动选中该路线下所有展开数据的复选框
        selectedRows.forEach(row => {
            const expandedData = expandedRowData[row.id];
            if (expandedData && expandedData.data.length > 0) {
                const allOrderIds = expandedData.data.map(detail => detail.orderId);
                setExpandedSelectedOrderIds(prev => ({
                    ...prev,
                    [row.id]: allOrderIds
                }));
            }
        });
        
        // 当取消选中路线时，取消选中该路线下所有展开数据的复选框
        const previousSelectedKeys = selectedRowKeys;
        const unselectedRows = previousSelectedKeys
            .filter(key => !selectedKeys.includes(key))
            .map(key => parcels?.find(p => p.id === key))
            .filter(Boolean) as DspReviewDetailType[];
        
        unselectedRows.forEach(row => {
            setExpandedSelectedOrderIds(prev => ({
                ...prev,
                [row.id]: []
            }));
        });
    };

    // 处理Suspected Missing操作
    const handleSuspectedMissing = async () => {
        // 收集所有选中的订单ID
        const orderIds: string[] = [];
        
        // 1. 收集主表格中选中行的订单ID（如果该行未展开，或者展开数据中没有选中项）
        for (const parcel of selectedParcels) {
            const expandedSelected = expandedSelectedOrderIds[parcel.id];
            if (expandedSelected && expandedSelected.length > 0) {
                // 如果该行展开数据中有选中项，跳过主表格的orderIdList
                continue;
            } else {
                // 如果该行未展开或展开数据中没有选中项，使用该行的orderIdList
                const expandedData = expandedRowData[parcel.id];
                if (expandedData && expandedData.data.length > 0) {
                    // 如果该行已展开，但展开数据中没有选中项，跳过
                    continue;
                } else {
                    // 如果该行未展开，使用该行的orderIdList
                    const parcelOrderIds = parcel.orderIdList.map(id => String(id));
                    orderIds.push(...parcelOrderIds);
                }
            }
        }
        
        // 2. 收集展开数据中选中的订单ID
        Object.values(expandedSelectedOrderIds).forEach(selectedOrderIds => {
            orderIds.push(...selectedOrderIds);
        });

        // 3. 去重订单ID
        const uniqueOrderIds = Array.from(new Set(orderIds));

        if (uniqueOrderIds.length === 0) {
            message.warning(t('wms.review.messages.pleaseSelectAtLeastOneParcel'));
            return;
        }

        try {
            const result = await batchMarkSuspectedMissingMutation({
                variables: {
                    orderIds: uniqueOrderIds,
                    exceptionHubId: siteType == SiteType.HUB ? currentWarehouse?.id! : currentWarehouse?.parentSiteId!,
                    exceptionWarehouseId: siteType == SiteType.WAREHOUSE ? currentWarehouse?.id! : undefined!
                }
            });

            if (result.data?.batchMarkSuspectedMissing) {
                message.success(t('wms.review.messages.successfullyMarkedParcelsAsSuspectedMissing', { count: uniqueOrderIds.length }));
                onClose();
            }
        } catch (error) {
            console.error('Suspected missing error:', error);
        } finally {
            setSelectedRowKeys([]);
            setSelectedParcels([]);
            setExpandedSelectedOrderIds({});
        }
    };

    // 包裹数据（不再需要过滤）
    const filteredParcels = parcels || [];

    // 处理行展开
    const handleExpand = (expanded: boolean, record: DspReviewDetailType) => {
        const rowKey = record.id;
        if (expanded) {
            // 每次展开都调用接口
            setExpandedRowData(prev => ({
                ...prev,
                [rowKey]: { loading: true, data: [] }
            }));
            
            getDeliveryTaskReviewDetail({
                variables: {
                    filter: {
                        accountId: record.driverAccountId
                    }
                }
            }).then((result) => {
                if (result.data?.getDeliveryTaskReviewDetail) {
                    // 按路径名称过滤，只显示与当前行的 routeName 匹配的数据
                    const filteredData = result.data.getDeliveryTaskReviewDetail
                        .filter(detail => detail.routeName === record.routeName)
                        .map(detail => ({
                            orderId: detail.orderId,
                            trackingNumber: detail.trackingNumber,
                            routeName: detail.routeName,
                            isTransferIn: detail.isTransferIn
                        }));
                    
                    setExpandedRowData(prev => ({
                        ...prev,
                        [rowKey]: {
                            loading: false,
                            data: filteredData
                        }
                    }));

                    // 如果该路线已经被选中，自动选中所有展开的数据
                    if (selectedRowKeys.includes(rowKey)) {
                        const allOrderIds = filteredData.map(detail => detail.orderId);
                        setExpandedSelectedOrderIds(prev => ({
                            ...prev,
                            [rowKey]: allOrderIds
                        }));
                    }
                }
            }).catch((error) => {
                console.error('Error fetching detail:', error);
                setExpandedRowData(prev => ({
                    ...prev,
                    [rowKey]: { loading: false, data: [] }
                }));
            });
            
            setExpandedRowKeys(prev => [...prev, rowKey]);
        } else {
            // 收起时，移除展开的行
            setExpandedRowKeys(prev => prev.filter(key => key !== rowKey));
        }
    };

    // 计算全选框的状态
    const allCheckboxState = useMemo(() => {
        if (!filteredParcels || filteredParcels.length === 0) {
            return { checked: false, indeterminate: false };
        }
        
        let checkedCount = 0;
        let indeterminateCount = 0;
        
        filteredParcels.forEach(record => {
            const checkboxState = getRowCheckboxState(record);
            if (checkboxState.checked) {
                checkedCount++;
            } else if (checkboxState.indeterminate) {
                indeterminateCount++;
            }
        });
        
        const totalCount = filteredParcels.length;
        if (checkedCount === totalCount && indeterminateCount === 0) {
            return { checked: true, indeterminate: false };
        } else if (checkedCount > 0 || indeterminateCount > 0) {
            return { checked: false, indeterminate: true };
        } else {
            return { checked: false, indeterminate: false };
        }
    }, [filteredParcels, expandedRowData, expandedSelectedOrderIds, selectedRowKeys]);

    // 处理全选框点击
    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            // 全选：选中所有行，并选中所有展开数据
            const allKeys = filteredParcels?.map(record => record.id) || [];
            setSelectedRowKeys(allKeys);
            setSelectedParcels(filteredParcels || []);
            
            // 选中所有展开数据
            filteredParcels?.forEach(record => {
                const expandedData = expandedRowData[record.id];
                if (expandedData && expandedData.data.length > 0) {
                    const allOrderIds = expandedData.data.map(detail => detail.orderId);
                    setExpandedSelectedOrderIds(prev => ({
                        ...prev,
                        [record.id]: allOrderIds
                    }));
                }
            });
        } else {
            // 取消全选：取消选中所有行和展开数据
            setSelectedRowKeys([]);
            setSelectedParcels([]);
            setExpandedSelectedOrderIds({});
        }
    };

    // 表格列定义
    const columns: ColumnsType<DspReviewDetailType> = [
        {
            title: t('wms.review.columns.area'),
            dataIndex: "area",
            width: 120,
        },
        {
            title: t('wms.review.columns.parcel') ,
            dataIndex: "unScanCount",
            width: 200,
        },
        {
            title: t('wms.review.columns.driverName'),
            dataIndex: "driverName",
            width: 240,
            align: 'left',
            render: (text: string, record) => (
                <div style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>
                    <span className={'font-bold'}>{text || t('wms.review.unknown')}</span>
                    <span className={'text-xs text-gray-500'}> +1 {record.driverPhone || t('wms.review.na')}</span>
                </div>
            )
        },
    ];

    return (
        <>
            <Drawer
                className="departure-dsp-review-drawer"
                title={title}
                width={960}
                open={visible}
                onClose={onClose}
                extra={null}
                bodyStyle={{
                    padding: '16px',
                    height: '100vh',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
            <div className="flex justify-end items-center mb-4 flex-shrink-0">
                <Button
                    type="primary"
                    onClick={handleSuspectedMissing}
                    loading={batchMarkAsExceptionLoading}
                    disabled={selectedRowKeys.length === 0 && Object.values(expandedSelectedOrderIds).every(ids => ids.length === 0)}
                    style={{ backgroundColor: '#8A2BE2', borderColor: '#8A2BE2', color: 'white' }}
                >
                    {t('wms.review.buttons.suspectedMissing')}
                </Button>
            </div>

            <div className="flex-1 overflow-hidden">
                <Table
                    dataSource={filteredParcels}
                    columns={columns}
                    rowKey="id"
                    pagination={false}
                    scroll={{y: 'calc(100vh - 200px)'}}
                    size="small"
                    loading={loading}
                    rowSelection={{
                        type: 'checkbox',
                        selectedRowKeys: useMemo(() => {
                            const keys: React.Key[] = [];
                            filteredParcels?.forEach(record => {
                                const checkboxState = getRowCheckboxState(record);
                                if (checkboxState.checked) {
                                    keys.push(record.id);
                                }
                            });
                            return keys;
                        }, [filteredParcels, expandedRowData, expandedSelectedOrderIds, selectedRowKeys]),
                        onChange: handleRowSelectionChange,
                        onSelectAll: (selected: boolean, selectedRows: DspReviewDetailType[], changeRows: DspReviewDetailType[]) => {
                            handleSelectAll(selected);
                        },
                        getCheckboxProps: (record: DspReviewDetailType) => {
                            const checkboxState = getRowCheckboxState(record);
                            return {
                                indeterminate: checkboxState.indeterminate,
                            };
                        },
                    }}
                    expandable={{
                        expandedRowKeys,
                        onExpand: handleExpand,
                        expandIconColumnIndex: 0, // 在复选框列内，复选框右侧
                        expandedRowRender: (record: DspReviewDetailType) => {
                            const rowData = expandedRowData[record.id];
                            if (!rowData) {
                                return <div style={{ padding: '16px', textAlign: 'center' }}>{t('wms.review.columns.loading') || 'Loading...'}</div>;
                            }
                            
                            if (rowData.loading) {
                                return <div style={{ padding: '16px', textAlign: 'center' }}>{t('wms.review.columns.loading') || 'Loading...'}</div>;
                            }

                            return (
                                <div style={{ background: 'white' }}>
                                    {rowData.data.map((detail) => {
                                        const isSelected = expandedSelectedOrderIds[record.id]?.includes(detail.orderId) || false;
                                        return (
                                            <div
                                                key={detail.orderId}
                                                style={{
                                                    display: 'table',
                                                    width: '100%',
                                                    borderBottom: '1px solid #f0f0f0',
                                                    background: 'white'
                                                }}
                                            >
                                                <div style={{ display: 'table-row' }}>
                                                    {/* 复选框列 - 与主表格复选框对齐（主表格复选框在展开图标之后，约62px位置） */}
                                                    <div style={{ 
                                                        display: 'table-cell',
                                                        width: '62px',
                                                        padding: '16px',
                                                        verticalAlign: 'middle'
                                                    }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={(e) => {
                                                                const currentSelected = expandedSelectedOrderIds[record.id] || [];
                                                                const newSelected = e.target.checked
                                                                    ? [...currentSelected, detail.orderId]
                                                                    : currentSelected.filter(id => id !== detail.orderId);
                                                                setExpandedSelectedOrderIds(prev => ({
                                                                    ...prev,
                                                                    [record.id]: newSelected
                                                                }));
                                                                
                                                                // 更新主表格行的复选框状态
                                                                const expandedData = expandedRowData[record.id];
                                                                if (expandedData && expandedData.data.length > 0) {
                                                                    const totalCount = expandedData.data.length;
                                                                    const selectedCount = newSelected.length;
                                                                    
                                                                    if (selectedCount === totalCount) {
                                                                        // 全部选中，选中主表格行
                                                                        if (!selectedRowKeys.includes(record.id)) {
                                                                            setSelectedRowKeys(prev => [...prev, record.id]);
                                                                            setSelectedParcels(prev => [...prev, record]);
                                                                        }
                                                                    } else if (selectedCount === 0) {
                                                                        // 全部取消，取消选中主表格行
                                                                        setSelectedRowKeys(prev => prev.filter(key => key !== record.id));
                                                                        setSelectedParcels(prev => prev.filter(p => p.id !== record.id));
                                                                    } else {
                                                                        // 部分选中，从 selectedRowKeys 中移除该行（因为它是 indeterminate 状态）
                                                                        // indeterminate 状态通过 getCheckboxProps 返回的 indeterminate 属性来显示
                                                                        setSelectedRowKeys(prev => prev.filter(key => key !== record.id));
                                                                        setSelectedParcels(prev => prev.filter(p => p.id !== record.id));
                                                                    }
                                                                }
                                                            }}
                                                            style={{ cursor: 'pointer' }}
                                                        />
                                                    </div>
                                                    {/* area列 - 空，与主表格area列对齐 */}
                                                    <div style={{ 
                                                        display: 'table-cell',
                                                        width: '120px',
                                                        padding: '16px',
                                                        verticalAlign: 'middle'
                                                    }}>
                                                    </div>
                                                    {/* 运单号列 - 与主表格"包裹"列对齐 */}
                                                    <div style={{
                                                        display: 'table-cell',
                                                        padding: '16px',
                                                        verticalAlign: 'middle',
                                                        textAlign: 'left'
                                                    }}>
                                                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                            <span
                                                                style={{ color: '#9254de', whiteSpace: 'nowrap', cursor: 'pointer' }}
                                                                onClick={() => {
                                                                    setSelectedOrderId(detail.orderId);
                                                                    setParcelDrawerVisible(true);
                                                                }}
                                                            >
                                                                {detail.trackingNumber}
                                                            </span>
                                                            {detail.isTransferIn && (
                                                                <Tag color="blue" style={{ marginLeft: 6, fontSize: 11, flexShrink: 0 }}>
                                                                    {t('wms.review.tag.transferIn')}
                                                                </Tag>
                                                            )}
                                                        </span>
                                                    </div>
                                                    {/* 司机名称列 - 与主表格"司机"列对齐 */}
                                                    <div style={{ 
                                                        display: 'table-cell',
                                                        width: '240px',
                                                        padding: '16px',
                                                        verticalAlign: 'middle',
                                                        textAlign: 'left'
                                                    }}>
                                                        <div style={{ textAlign: 'left', margin: 0, padding: 0, whiteSpace: 'nowrap' }}>
                                                            <span className={'font-bold'}>
                                                                {record.driverName || t('wms.review.unknown') || 'Unknown'}
                                                            </span>
                                                            <span className={'text-xs text-gray-500'}>
                                                                {' +1 '}{record.driverPhone || t('wms.review.na') || 'N/A'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        },
                    }}
                />
            </div>

            {selectedOrderId && (
                <CentralParcelDetailDrawer
                    showInterceptionExemptionAlert={true}
                    visible={parcelDrawerVisible}
                    onClose={() => {
                        setParcelDrawerVisible(false);
                        setSelectedOrderId(null);
                    }}
                    orderId={selectedOrderId}
                    userRole={getUserInfo()?.account?.role?.name || ''}
                />
            )}
        </Drawer>
        </>
    );
};

export default DepartureDspReviewDrawer;

